import getDecorators from "inversify-inject-decorators";
import { SimpleGame } from "../../../app";
import container from "../../../ioc/inversify.config";
import TYPES from "../../../ioc/types";
import { AuthenticatedAssetLoader } from "../../../services/authenticated-asset-loader";
import { DefaultEventService } from "../../../services/event-service";
import { IMessageQueueService, MessageWithCallback } from "../../../services/message-queue-service";
import { Asset, BattleContextParams, BattleParams } from "../../../shared/domain";
import {
    BattleContextV2DTO,
    BattleEndDTO,
    CombatCharacterDTO,
    PlayerInputRequestDTO,
    StatDTO,
} from "../../../shared/dtos";
import { Endpoints, Events, getAssetUrl, SceneId, SocketEvents } from "../../../shared/enum";
import { LevelUpNotification } from "../../components/level-up-notification";

const { lazyInject } = getDecorators(container);

export class BattleScene extends Phaser.Scene {
    private battleContext: BattleContextV2DTO;
    private stagedBattleContext: BattleContextV2DTO; // Progressive HP updates during message display

    public heroes: PlayerCharacterSprite[];
    public enemies: EnemySprite[];

    private origin: string;

    private units: Unit[];
    private activeUnit: number;

    private BACKGROUND_COLOR = "rgba(0, 200, 0, 0.5)";
    private PLAYERS_X_POSITION =
        SimpleGame.GAME_OPTIONS.screenWidth - 50 * SimpleGame.GAME_OPTIONS.zoom;
    private PLAYERS_TOP_POSITION = 50;
    private ENEMY_X_POSITION = 25 * SimpleGame.GAME_OPTIONS.zoom;
    private ENEMY_Y_POSITION = 50;

    private BATTLE_DELAY = 2000;

    private assets: Asset[];
    //    private iosocket = null;

    //    @lazyInject(TYPES.SocketIOService)
    //    private socketService: DefaultSocketIOService;

    @lazyInject(TYPES.EventService)
    private eventService: DefaultEventService;

    @lazyInject(TYPES.MessageQueueService)
    private messageQueueService: IMessageQueueService;

    @lazyInject(TYPES.AuthenticatedAssetLoader)
    private assetLoader: AuthenticatedAssetLoader;

    constructor() {
        super({ key: SceneId.BATTLE_SCENE });
        //        console.log('battle constructor');
        //        this.iosocket = this.socketService.getConnection();

        this.bindSocketEvents();
    }

    private bindSocketEvents() {
        // Parse and instantiate DTOs immediately upon receiving socket events
        this.eventService.register(SocketEvents.PLAYER_TURN_RESULT, (data: string) => {
            const battleContext = BattleContextV2DTO.fromJson(JSON.parse(data));
            this.onAttackResult(battleContext);
        });

        this.eventService.register(SocketEvents.BATTLE_STARTED, (data: string) => {
            const battleContext = BattleContextV2DTO.fromJson(JSON.parse(data));
            this.onBattleStart(battleContext);
        });

        this.eventService.register(SocketEvents.PLAYER_INPUT, (data: string) => {
            // Parse JSON string before creating DTO
            const playerInputRequest = PlayerInputRequestDTO.fromJson(JSON.parse(data));
            this.onPlayerInput(playerInputRequest);
        });

        this.eventService.register(SocketEvents.END_BATTLE, (data?: string) => {
            const battleEnd = data ? BattleEndDTO.fromJson(JSON.parse(data)) : null;
            this.endBattle(battleEnd);
        });

        this.eventService.register(SocketEvents.ATTACK_NOTIFICATION, (data: string) => {
            this.notifyAttack(data);
        });
    }

    init(params: BattleContextParams) {
        // Remove before adding to prevent duplicate registrations if init() is called again
        this.events.off(Events.ATTACK, this.receivePlayerSelection, this);
        this.events.on(Events.ATTACK, this.receivePlayerSelection, this);
        this.assets = params.assets;
        this.origin = params.origin;
    }

    preload() {
        this.assets.forEach((asset) => {
            if (asset.category !== "tileset" && asset.onlineImage) {
                const width = asset.spriteWidth ? asset.spriteWidth : 32;
                const height = asset.spriteHeight ? asset.spriteHeight : 32;
                this.load.spritesheet({
                    key: asset.filename,
                    url: getAssetUrl(asset.onlineImage),
                    frameConfig: { frameWidth: width, frameHeight: height },
                });
            }
        });
        // Note: Unit sprites (both player and enemy) are now loaded dynamically in preloadUnitSprites()
    }

    create(param) {
        // Restore camera visibility (clearBattle sets alpha to 0 to hide the empty
        // green background during the queued scene transition at battle end).
        this.cameras.main.setAlpha(1);
        // change the background to green
        this.cameras.main.setBackgroundColor(this.BACKGROUND_COLOR);

        this.startBattle(param.origin);
        // on wake event we call startBattle too — remove before adding to prevent
        // duplicate registrations if create() is somehow called more than once.
        this.sys.events.off("wake", this.startBattle, this);
        this.sys.events.on("wake", this.startBattle, this);
    }

    public startBattle(sceneId: string) {
        // Ensure camera is fully visible (may have been hidden in clearBattle).
        this.cameras.main.setAlpha(1);
        // Reset all battle state before requesting a new battle
        this.heroes = [];
        this.enemies = [];
        this.units = [];
        this.activeUnit = -1;
        this.battleContext = null;
        this.stagedBattleContext = null;
        this.messageQueueService.clear();

        this.eventService.emit(SocketEvents.START_BATTLE, this.origin);
        console.log("socket emitted battle start");
    }

    private clearUnits() {
        this.heroes = [];
        this.enemies = [];
        this.units.forEach((unit) => unit.destroy());
        this.units = [];
    }

    /**
     * Preload all unit sprites using online_image URLs.
     * Fetches authenticated images as blob URLs before passing to Phaser's loader.
     */
    private async preloadUnitSprites(callback: () => void) {
        const allUnits = [...this.battleContext.player_units, ...this.battleContext.enemy_units];

        for (const unit of allUnits) {
            const spriteData = unit.sprite_data;
            if (!spriteData.online_image) continue;

            const imageUrl = getAssetUrl(spriteData.online_image);
            const key = spriteData.online_image;

            let url = imageUrl;
            console.log("trying to load ", imageUrl);
            if (imageUrl.includes("/api/v1/images/")) {
                try {
                    url = await this.assetLoader.fetchAsBlob(imageUrl);
                    console.log(`Successfully fetched blob URL for ${key}`);
                } catch (err) {
                    console.error(`Failed to fetch unit sprite ${key}:`, err);
                    continue;
                }
            }

            this.load.spritesheet({
                key,
                url,
                frameConfig: {
                    frameWidth: spriteData.width || 32,
                    frameHeight: spriteData.height || 64,
                },
            });
        }

        this.load.once("complete", callback);
        this.load.start();
    }

    /**
     * with recent changes, it's probably possible to completely merge heroes & enemies as units
     */
    private createUnitSprites() {
        this.createUnitSpritesFromContext(this.battleContext);
    }

    private createUnitSpritesFromContext(context: BattleContextV2DTO) {
        // Create player character sprites
        context.player_units.forEach((unit, index) => {
            const sprite = new PlayerCharacterSprite(
                this,
                this.PLAYERS_X_POSITION,
                this.PLAYERS_TOP_POSITION * (index + 1),
                unit
            );
            if (!unit.alive) {
                sprite.active = false;
                sprite.visible = false;
            }
            this.heroes.push(sprite);
        });

        // Create enemy sprites
        context.enemy_units.forEach((unit, index) => {
            const sprite = new EnemySprite(
                this,
                this.ENEMY_X_POSITION,
                this.ENEMY_Y_POSITION * (index + 1),
                unit
            );
            if (!unit.alive) {
                sprite.active = false;
                sprite.visible = false;
            }
            this.enemies.push(sprite);
        });

        this.units = this.heroes.concat(this.enemies);
        this.units.forEach((unit) => {
            this.add.existing(unit);
        });
    }

    // when the player have selected the enemy to be attacked
    public receivePlayerSelection(action: string, target: number) {
        //        console.log('player input: ', action, target);
        if (action === "attack") {
            this.attack(this.enemies[target].unitModel);
        }
        // next turn in 3 seconds
        //        this.time.addEvent({ delay: this.BATTLE_DELAY, callback: this.nextTurn, callbackScope: this });
    }

    public clearDeadUnits() {
        console.log("clearing dead units: ", this.units);
        this.units.forEach((unit) => {
            if (!unit.unitModel.alive) {
                unit.destroy();
            }
        });
        this.events.emit("refresh");
    }

    private clearBattle() {
        // Hide this camera immediately so the empty green background is not visible
        // for the 1-2 frames it takes Phaser to process the queued scene.stop / scene.switch.
        this.cameras.main.setAlpha(0);

        // clear state, remove sprites
        this.heroes.length = 0;
        this.enemies.length = 0;
        this.units.forEach((unit) => unit.destroy());
        this.units.length = 0;
        //        this.iosocket.disconnect();
        // Stop the UI scene completely so it gets fully recreated next battle.
        // Using sleep() would keep the old SideBattleMenu whose event listeners
        // on battleScene.events were removed during BattleScene shutdown/restart.
        this.scene.stop(SceneId.BATTLE_MENU_SCENE);
        // return to WorldScene and sleep current BattleScene
        console.log("switching back to map: ", this.origin);
        this.scene.switch(this.origin);
    }

    public onAttackResult(battleContext: BattleContextV2DTO) {
        console.log("ON ATTACK RESULT: ", battleContext);

        // Guard: ignore stale results if no active battle
        if (!this.battleContext) {
            console.warn("onAttackResult: No active battle context, ignoring stale result");
            return;
        }

        // Guard: ignore results from a different battle
        if (this.battleContext.battle_id !== battleContext.battle_id) {
            console.warn(
                `onAttackResult: Battle ID mismatch (current: ${this.battleContext.battle_id}, received: ${battleContext.battle_id}), ignoring`
            );
            return;
        }

        // Store the previous battle context to preserve old HP values
        const previousBattleContext = this.battleContext;

        // Update the final battle context
        this.battleContext = battleContext;

        // Create a staged context that will be updated progressively
        // Start with old HP values if we have a previous context
        if (previousBattleContext) {
            this.stagedBattleContext = this.cloneBattleContext(previousBattleContext);
        } else {
            // First battle update - start with current values
            this.stagedBattleContext = this.cloneBattleContext(battleContext);
        }

        // Update sprites to show staged context (old HP values initially)
        this.updateFromStagedContext();
        this.events.emit(Events.REFRESH_BATTLE_MENU, this.activeUnit);

        // Process turn_log entries with progressive HP updates
        const messagesWithCallbacks = this.processTurnLogWithCallbacks(battleContext.turn_log);

        if (messagesWithCallbacks.length > 0) {
            console.log(
                `BattleScene: Processing ${messagesWithCallbacks.length} turn messages with HP updates`
            );

            // FIX: Only block input if queue actually accepts the messages
            if (!this.messageQueueService.isPlaying()) {
                // Block input during message playback
                this.events.emit(Events.BLOCK_BATTLE_INPUT);

                // Queue messages with callbacks - HP updates will happen as messages display
                this.messageQueueService.enqueueMessagesWithCallbacks(
                    messagesWithCallbacks,
                    this.events,
                    this,
                    1500
                );

                // Wait for all messages to display before enabling input
                this.messageQueueService.onQueueComplete(() => {
                    // Ensure staged context matches final context
                    this.stagedBattleContext = this.cloneBattleContext(this.battleContext);
                    this.updateFromStagedContext();
                    this.events.emit(Events.REFRESH_BATTLE_MENU, this.activeUnit);

                    console.log("BattleScene: All turn messages processed, unblocking input");
                    this.events.emit(Events.UNBLOCK_BATTLE_INPUT);
                    // Request input for the active unit
                    if (this.battleContext.active_unit_id) {
                        const playerInputRequest = new PlayerInputRequestDTO({
                            active_unit_id: this.battleContext.active_unit_id,
                            active_unit: this.battleContext.findUnitById(
                                this.battleContext.active_unit_id
                            ),
                        });
                        this.onPlayerInput(playerInputRequest);
                    }
                });
            } else {
                console.warn("BattleScene: Queue already processing, skipping messages");
            }
        } else {
            // No turn messages, update to final state immediately
            console.log("BattleScene: No turn messages, proceeding immediately");
            this.stagedBattleContext = this.cloneBattleContext(this.battleContext);
            this.updateFromStagedContext();
            this.events.emit(Events.REFRESH_BATTLE_MENU, this.activeUnit);

            if (this.battleContext.active_unit_id) {
                const playerInputRequest = new PlayerInputRequestDTO({
                    active_unit_id: this.battleContext.active_unit_id,
                    active_unit: this.battleContext.findUnitById(this.battleContext.active_unit_id),
                });
                this.onPlayerInput(playerInputRequest);
            }
        }
    }

    private processTurnLog(turnLog: any[]): string[] {
        // Convert structured turn_log entries to display messages
        const messages: string[] = [];

        if (!turnLog || !Array.isArray(turnLog)) {
            return messages;
        }

        turnLog.forEach((entry) => {
            switch (entry.type) {
                case "damage":
                    messages.push(
                        `${entry.attacker} deals ${entry.damage} damage to ${entry.target}!`
                    );
                    if (!entry.alive) {
                        messages.push(`${entry.target} has been defeated!`);
                    }
                    break;
                case "victory":
                    messages.push(entry.message);
                    break;
                case "defeat":
                    messages.push(entry.message);
                    break;
                case "xp_gain":
                    // Format XP gain message (will be highlighted in processTurnLogWithCallbacks)
                    messages.push(`${entry.character_name} gained ${entry.xp_amount} XP!`);
                    break;
                // Add other action types as implemented
            }
        });

        return messages;
    }

    private processTurnLogWithCallbacks(turnLog: any[]): MessageWithCallback[] {
        // Convert structured turn_log entries to messages with HP update callbacks
        const messagesWithCallbacks: MessageWithCallback[] = [];

        if (!turnLog || !Array.isArray(turnLog)) {
            return messagesWithCallbacks;
        }

        turnLog.forEach((entry) => {
            switch (entry.type) {
                case "damage":
                    // Create damage message with callback to update HP
                    messagesWithCallbacks.push({
                        message: `${entry.attacker} deals ${entry.damage} damage to ${entry.target}!`,
                        callback: () =>
                            this.applyDamageToStagedContext(
                                entry.target,
                                entry.damage,
                                entry.alive
                            ),
                    });

                    // Add defeat message if unit died (no callback needed, just visual)
                    if (!entry.alive) {
                        messagesWithCallbacks.push({
                            message: `${entry.target} has been defeated!`,
                        });
                    }
                    break;
                case "victory":
                    messagesWithCallbacks.push({
                        message: entry.message,
                    });
                    break;
                case "defeat":
                    messagesWithCallbacks.push({
                        message: entry.message,
                    });
                    break;
                case "xp_gain":
                    // XP gain message - highlighted in a different color
                    messagesWithCallbacks.push({
                        message: `${entry.character_name} gained ${entry.xp_amount} XP!`,
                        // No callback needed - XP is applied on backend
                    });
                    break;
                // Add other action types as implemented
            }
        });

        return messagesWithCallbacks;
    }

    private cloneBattleContext(context: BattleContextV2DTO): BattleContextV2DTO {
        // Deep clone the battle context to avoid mutating the original
        return BattleContextV2DTO.fromJson(JSON.parse(JSON.stringify(context)));
    }

    private applyDamageToStagedContext(targetName: string, damage: number, alive: boolean): void {
        // Find the unit by name in staged context and apply damage
        const allUnits = this.stagedBattleContext.getAllUnits();
        const targetUnit = allUnits.find((unit) => unit.name === targetName);

        if (targetUnit) {
            const hpStat = targetUnit.findStatById("hp");
            if (hpStat) {
                // Apply damage
                hpStat.value = Math.max(0, hpStat.value - damage);
                console.log(`Applied ${damage} damage to ${targetName}. New HP: ${hpStat.value}`);
            }

            // Update alive status
            targetUnit.alive = alive;

            // Refresh display to show updated HP
            this.updateFromStagedContext();
            this.events.emit(Events.REFRESH_BATTLE_MENU, this.activeUnit);
        } else {
            console.warn(`Could not find unit with name: ${targetName}`);
        }
    }

    private updateFromStagedContext(): void {
        // Update sprites and UI based on staged context (progressive HP)
        this.clearUnits();
        this.createUnitSpritesFromContext(this.stagedBattleContext);
    }

    public async endBattle(battleEnd: BattleEndDTO | null) {
        // FIX: Clear message queue immediately to prevent race conditions
        console.log("BattleScene: Clearing message queue before ending battle");
        this.messageQueueService.clear();
        // Unblock input in case it was blocked during message playback
        this.events.emit(Events.UNBLOCK_BATTLE_INPUT);

        if (battleEnd) {
            console.log("Battle ended with state:", battleEnd.state);
            console.log("Rewards:", battleEnd.rewards);

            // Check for level-ups and display notifications
            if (battleEnd.level_ups && battleEnd.level_ups.length > 0) {
                console.log("Processing level-ups:", battleEnd.level_ups);

                // Display level-up notifications sequentially
                for (const levelUp of battleEnd.level_ups) {
                    const notification = new LevelUpNotification(
                        this,
                        this.cameras.main.centerX,
                        this.cameras.main.centerY,
                        levelUp.character_name,
                        levelUp.new_level,
                        levelUp.stat_increases
                    );

                    // Wait for user to confirm before showing next level-up
                    await notification.waitForConfirm();
                }
            }
        } else {
            console.log("Battle ended (legacy event without data)");
            console.log("Rewards:", this.battleContext?.rewards);
        }
        this.clearBattle();
    }

    public onBattleStart(battleContext: BattleContextV2DTO) {
        console.log("Received on_battle_start");

        // SAFEGUARD: Clear any lingering messages from previous battle
        this.messageQueueService.clear();

        this.battleContext = battleContext;
        this.stagedBattleContext = null;

        // Debug logging to check data structure
        console.log("=== BATTLE CONTEXT DEBUG ===");
        console.log("Player units:", battleContext.player_units);
        console.log("Enemy units:", battleContext.enemy_units);

        if (battleContext.player_units.length > 0) {
            console.log("First player unit details:");
            console.log("  - type:", battleContext.player_units[0].type);
            console.log("  - name:", battleContext.player_units[0].name);
            console.log("  - sprite_data:", battleContext.player_units[0].sprite_data);
            console.log("  - stat_container:", battleContext.player_units[0].stat_container);
        }

        if (battleContext.enemy_units.length > 0) {
            console.log("First enemy unit details:");
            console.log("  - type:", battleContext.enemy_units[0].type);
            console.log("  - name:", battleContext.enemy_units[0].name);
            console.log("  - sprite_data:", battleContext.enemy_units[0].sprite_data);
            console.log("  - stat_container:", battleContext.enemy_units[0].stat_container);
        }
        console.log("=========================");

        // Preload all unit sprites before creating them
        this.preloadUnitSprites(() => {
            this.createUnitSprites();
            this.activeUnit = -1; // currently active unit
            console.log("Battle context loaded:", battleContext);
            this.scene.run(SceneId.BATTLE_MENU_SCENE, { scene: this } as BattleParams);
        });
    }

    public onPlayerInput(playerInputRequest: PlayerInputRequestDTO) {
        console.log(
            "onPlayerInput - active_unit_id received from backend:",
            playerInputRequest.active_unit_id
        );
        console.log("Active unit details:", playerInputRequest.active_unit);

        // Skip if message queue is still playing — the onQueueComplete callback
        // will call onPlayerInput again once messages finish displaying.
        if (this.messageQueueService.isPlaying()) {
            console.log(
                "onPlayerInput: Deferring — message queue still playing, will be called from onQueueComplete"
            );
            return;
        }

        const activeUnitId = playerInputRequest.active_unit_id;

        // Find the index of the hero with this character_id
        const heroIndex = this.heroes.findIndex(
            (hero) => hero.unitModel.character_id === activeUnitId
        );

        if (heroIndex === -1) {
            console.warn(`Could not find hero with character_id: ${activeUnitId}`);
            return;
        }

        console.log(`Found hero at index ${heroIndex} for character_id: ${activeUnitId}`);
        this.events.emit("PlayerSelect", heroIndex);
    }

    private notifyAttack(data: string) {
        //        const notification = JSON.parse(data) as NotificationDTO;
        this.events.emit(Events.MESSAGE, data);
    }

    private attack(defender: CombatCharacterDTO) {
        //        console.log('attack ! ');
        this.eventService.emit(SocketEvents.ATTACK, {
            battle_id: this.battleContext.battle_id,
            target_combat_id: defender.combat_id,
        });
    }

    private updateFromBattleContext() {
        this.clearUnits();
        this.createUnitSprites();
    }

    public shutdown() {
        console.log("BattleScene: Shutting down, clearing message queue");
        this.messageQueueService.clear();
        this.events.off(Events.ATTACK, this.receivePlayerSelection, this);
        this.sys.events.off("wake", this.startBattle, this);
    }
}

// base class for heroes and enemies
export class Unit extends Phaser.GameObjects.Sprite {
    protected _unitModel: CombatCharacterDTO;

    constructor(scene: Phaser.Scene, x: number, y: number, unitModel: CombatCharacterDTO) {
        const frameIndex = unitModel.sprite_data.battle_frame_name
            ? unitModel.sprite_data.battle_frame_name
            : unitModel.frame_index;
        // Use online_image as sprite key (preloaded in preloadUnitSprites)
        const spriteKey = unitModel.sprite_data.online_image || unitModel.sprite_data.image_path;
        super(scene, x, y, spriteKey, frameIndex);
        this._unitModel = unitModel;
    }

    public findStatById(statId: string): StatDTO {
        return this.unitModel.findStatById(statId);
    }

    public destroy() {
        super.destroy();
    }

    get unitModel(): CombatCharacterDTO {
        return this._unitModel;
    }

    public toString() {
        return (this.unitModel.character_id + ": ", this.unitModel.alive);
    }
}

export class EnemySprite extends Unit {
    constructor(scene: Phaser.Scene, x: number, y: number, unitModel: CombatCharacterDTO) {
        super(scene, x, y, unitModel);
    }
}

export class PlayerCharacterSprite extends Unit {
    constructor(scene: Phaser.Scene, x: number, y: number, unitModel: CombatCharacterDTO) {
        super(scene, x, y, unitModel);
        // flip the image so I don"t have to edit it manually
        //        this.flipX = true;

        //        this.setScale(2);
    }
}
